import { getAssessmentEmailError } from "./assessment-contact";

const MODES = new Set(["residential", "business", "general"]);
const AUDIENCES = new Set(["homeowner", "business", "commercial", "industrial", "general"]);
const SERVICES = new Set([
  "general",
  "residential",
  "water-softener",
  "uv-system",
  "business-water",
  "plc-industrial",
  "location-service",
  "water-hardness",
  "product-guidance",
  "project-planning",
  "services-hub",
  "home-system-recommendation",
  "commercial-water-treatment",
  "plc-industrial-programming",
  "locations",
]);
const PLACEMENTS = new Set([
  "hero",
  "inline",
  "mid-page",
  "result",
  "endcap",
  "home_endcap",
  "article",
  "project",
  "location-primary",
  "locations-hub",
  "diagnostic-launcher",
]);
const DIAGNOSTIC_TOOLS = new Set(["water-hardness", "help-me-choose"]);
const HARDNESS_LEVELS = new Set(["Soft", "Moderate", "Hard", "Very Hard", "Unknown"]);
const QUIZ_CONCERNS = new Set(["scale", "chlorine", "drinking", "staining", "bacteria", "not-sure"]);
const QUIZ_WATER_SOURCES = new Set(["city", "well", "not-sure"]);
const QUIZ_COVERAGE = new Set(["whole-home", "drinking-water", "both"]);
const QUIZ_HOUSEHOLDS = new Set(["1-2", "3-5", "6+"]);

const ASSESSMENT_TYPES = new Set([
  "home-assessment",
  "home-quick-assessment",
  "business-assessment",
  "business-quick-assessment",
]);
const CONTACT_TYPES = new Set(["contact", "quick-contact"]);
const CONTACT_INTENTS = new Set([
  "product-selection",
  "existing-system-service",
  "warranty-support",
  "business-project",
  "showroom-visit",
  "general",
]);
const PROPERTY_TYPES = new Set([
  "detached-house",
  "apartment",
  "residential",
  "townhouse",
  "condominium",
  "bungalow",
  "commercial",
  "agricultural",
]);
const WATER_SOURCES = new Set(["tap-water", "well-water"]);
const WATER_ISSUES = new Set([
  "hard-water",
  "bad-taste",
  "unpleasant-odor",
  "staining-issues",
  "low-pressure",
  "sediment",
  "drinking-water",
  "well-water",
  "chlorine",
  "not-sure",
]);
const CONTACT_METHODS = new Set(["phone", "email", "text"]);
const PROGRAM_INTERESTS = new Set([
  ...SERVICES,
  "whole-bundle",
  "filter-bundle",
  "replacement",
]);
const BUSINESS_APPLICATIONS = new Set([
  "drinking-water",
  "equipment-protection",
  "process-water",
  "source-water-safety",
  "automation-controls",
  "existing-system-service",
  "not-sure",
]);
const FACILITY_TYPES = new Set([
  "restaurant-hospitality",
  "office-retail",
  "healthcare-lab",
  "manufacturing-processing",
  "multi-residential",
  "agriculture-rural",
  "other",
]);

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CONTROL_CHARACTERS = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g;

function cleanText(value, maxLength = 120) {
  if (typeof value !== "string") return undefined;
  const cleaned = value.replace(CONTROL_CHARACTERS, "").trim();
  return cleaned ? cleaned.slice(0, maxLength) : undefined;
}

function cleanEnum(value, allowedValues) {
  const cleaned = cleanText(value, 100);
  return cleaned && allowedValues.has(cleaned) ? cleaned : undefined;
}

function cleanStringList(value, allowedValues, maxItems = 8) {
  if (!Array.isArray(value)) return [];
  return [...new Set(value.map((item) => cleanEnum(item, allowedValues)).filter(Boolean))].slice(0, maxItems);
}

function cleanEmail(value) {
  const email = cleanText(value, 254);
  return email && EMAIL_PATTERN.test(email) ? email : undefined;
}

function cleanPhone(value) {
  const phone = cleanText(value, 40);
  if (!phone) return undefined;
  const digits = phone.replace(/\D/g, "");
  return digits.length >= 10 && digits.length <= 15 ? phone : undefined;
}

function cleanBoolean(value) {
  if (value === true || value === "true") return true;
  if (value === false || value === "false") return false;
  return undefined;
}

function cleanPositiveInteger(value, max = 30) {
  if (value === "" || value === null || value === undefined) return undefined;
  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) && parsed >= 1 && parsed <= max ? parsed : undefined;
}

function cleanDateTime(value) {
  const cleaned = cleanText(value, 40);
  return cleaned && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(cleaned) ? cleaned : undefined;
}

function compactObject(value) {
  return Object.fromEntries(
    Object.entries(value).filter(([, item]) => (
      item !== undefined &&
      item !== null &&
      item !== "" &&
      (!Array.isArray(item) || item.length > 0)
    )),
  );
}

export function sanitizeDiagnosticContext(value = {}) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;

  const tool = cleanEnum(value.tool, DIAGNOSTIC_TOOLS);
  if (!tool) return undefined;

  if (tool === "water-hardness") {
    return compactObject({
      tool,
      province: cleanText(value.province, 80),
      city: cleanText(value.city, 80),
      hardness: cleanText(value.hardness, 80),
      hardnessLevel: cleanEnum(value.hardnessLevel, HARDNESS_LEVELS),
      resultId: cleanText(value.resultId, 120),
      version: cleanText(value.version, 30),
    });
  }

  return compactObject({
    tool,
    concerns: cleanStringList(value.concerns, QUIZ_CONCERNS, 2),
    waterSource: cleanEnum(value.waterSource, QUIZ_WATER_SOURCES),
    coverage: cleanEnum(value.coverage, QUIZ_COVERAGE),
    household: cleanEnum(value.household, QUIZ_HOUSEHOLDS),
    resultId: cleanText(value.resultId, 120),
    assessmentPriority: cleanEnum(value.assessmentPriority, new Set(["high", "recommended", "optional"])),
    version: cleanText(value.version, 30),
  });
}

export function sanitizeConsultationContext(value = {}) {
  const raw = value && typeof value === "object" && !Array.isArray(value) ? value : {};
  const mode = MODES.has(raw.mode) ? raw.mode : "general";
  const audience = AUDIENCES.has(raw.audience) ? raw.audience : mode === "business" ? "business" : mode === "residential" ? "homeowner" : "general";
  const service = SERVICES.has(raw.service) ? raw.service : "general";
  const placement = PLACEMENTS.has(raw.placement) ? raw.placement : "inline";
  const pagePath = cleanText(raw.pagePath || raw.page_path, 240);
  const safePagePath = pagePath?.startsWith("/") && !pagePath.includes("//") ? pagePath : undefined;
  const diagnostic = sanitizeDiagnosticContext(raw.diagnostic);

  return compactObject({
    mode,
    audience,
    service,
    placement,
    pagePath: safePagePath,
    city: cleanText(raw.city, 80),
    contentId: cleanText(raw.contentId || raw.content_id, 100),
    ctaVariant: cleanText(raw.ctaVariant || raw.cta_variant, 80),
    diagnostic,
  });
}

export function formatConsultationContext(value = {}) {
  const context = sanitizeConsultationContext(value);
  return [
    "Quick consultation request",
    `Audience: ${context.audience}`,
    `Service: ${context.service}`,
    `Placement: ${context.placement}`,
    context.pagePath ? `Page: ${context.pagePath}` : null,
    context.city ? `City: ${context.city}` : null,
    context.contentId ? `Content: ${context.contentId}` : null,
    context.ctaVariant ? `CTA variant: ${context.ctaVariant}` : null,
    context.diagnostic?.tool ? `Diagnostic tool: ${context.diagnostic.tool}` : null,
    context.diagnostic?.concerns?.length ? `Concerns: ${context.diagnostic.concerns.join(", ")}` : null,
    context.diagnostic?.waterSource ? `Water source answer: ${context.diagnostic.waterSource}` : null,
    context.diagnostic?.coverage ? `Coverage answer: ${context.diagnostic.coverage}` : null,
    context.diagnostic?.household ? `Household answer: ${context.diagnostic.household}` : null,
    context.diagnostic?.province ? `Province: ${context.diagnostic.province}` : null,
    context.diagnostic?.city ? `Result city: ${context.diagnostic.city}` : null,
    context.diagnostic?.hardness ? `Hardness result: ${context.diagnostic.hardness}` : null,
    context.diagnostic?.hardnessLevel ? `Hardness level: ${context.diagnostic.hardnessLevel}` : null,
    context.diagnostic?.resultId ? `Recommendation: ${context.diagnostic.resultId}` : null,
    context.diagnostic?.assessmentPriority ? `Assessment priority: ${context.diagnostic.assessmentPriority}` : null,
    context.diagnostic?.version ? `Rule version: ${context.diagnostic.version}` : null,
  ].filter(Boolean).join("\n");
}

export function sanitizeContactSubmission(value = {}) {
  const raw = value && typeof value === "object" && !Array.isArray(value) ? value : {};
  const errors = {};
  const type = cleanEnum(raw.type, CONTACT_TYPES) || "contact";
  const firstName = cleanText(raw.first_name || raw.firstName, 80);
  const phone = cleanPhone(raw.phone);
  const rawEmail = cleanText(raw.email, 254);

  if (!firstName) errors.first_name = "Enter your first name.";
  if (!phone) errors.phone = "Enter a valid phone number with 10 to 15 digits.";
  if (rawEmail && !cleanEmail(rawEmail)) errors.email = "Enter a valid email address.";

  return {
    data: compactObject({
      type,
      first_name: firstName,
      last_name: cleanText(raw.last_name || raw.lastName, 80),
      email: cleanEmail(rawEmail),
      phone,
      customer_note: cleanText(raw.customer_note || raw.note, 2000),
      contact_intent: cleanEnum(raw.contact_intent || raw.intent, CONTACT_INTENTS) || "general",
      consultation_context: raw.consultation_context
        ? sanitizeConsultationContext(raw.consultation_context)
        : undefined,
    }),
    errors,
  };
}

export function sanitizeAssessmentSubmission(value = {}) {
  const raw = value && typeof value === "object" && !Array.isArray(value) ? value : {};
  const errors = {};
  const type = cleanEnum(raw.type, ASSESSMENT_TYPES) || "home-assessment";
  const isBusiness = type.startsWith("business-");
  const isQuick = type.includes("quick");
  const firstName = cleanText(raw.first_name || raw.firstName, 80);
  const phone = cleanPhone(raw.phone);
  const companyName = cleanText(raw.company_name || raw.companyName, 160);
  const application = cleanEnum(raw.application, BUSINESS_APPLICATIONS);
  const rawEmail = cleanText(raw.email, 254);
  const preferredContactMethod = cleanEnum(raw.preferred_contact_method, CONTACT_METHODS);

  if (!firstName) errors.first_name = "Enter your first name.";
  if (!phone) errors.phone = "Enter a valid phone number with 10 to 15 digits.";
  if (isBusiness && !companyName) errors.company_name = "Enter your company name.";
  if (isBusiness && !isQuick && !application) errors.application = "Select the main application.";
  const emailError = getAssessmentEmailError(raw.email, preferredContactMethod);
  if (emailError) errors.email = emailError;

  const householdSize = cleanPositiveInteger(raw.household_size);
  if (raw.household_size && !householdSize) errors.household_size = "Enter a household size between 1 and 30.";

  return {
    data: compactObject({
      type,
      first_name: firstName,
      last_name: cleanText(raw.last_name || raw.lastName, 80),
      email: cleanEmail(rawEmail),
      phone,
      company_name: companyName,
      address: cleanText(raw.address, 300),
      property_type: cleanEnum(raw.property_type, PROPERTY_TYPES),
      household_size: householdSize,
      water_source: cleanStringList(raw.water_source, WATER_SOURCES, 2),
      water_issues: cleanStringList(raw.water_issues, WATER_ISSUES, 8),
      finished_basement: cleanBoolean(raw.finished_basement),
      water_meter_in_equipment_room: cleanBoolean(raw.water_meter_in_equipment_room),
      preferred_contact_method: preferredContactMethod,
      existing_water_treatment_systems: cleanText(raw.existing_water_treatment_systems, 500),
      program_interest: cleanEnum(raw.program_interest, PROGRAM_INTERESTS),
      application,
      facility_type: cleanEnum(raw.facility_type, FACILITY_TYPES),
      target_flow: cleanText(raw.target_flow, 100),
      project_details: cleanText(raw.project_details, 2000),
      consultation_window: cleanDateTime(raw.consultation_window || raw.service_time),
      customer_note: cleanText(raw.customer_note, 2000),
      consultation_context: raw.consultation_context
        ? sanitizeConsultationContext(raw.consultation_context)
        : undefined,
    }),
    errors,
  };
}

export function formatBusinessRequestDetails(value = {}) {
  const details = [
    value.application ? `Application: ${value.application}` : null,
    value.facility_type ? `Facility type: ${value.facility_type}` : null,
    value.target_flow ? `Target flow or demand: ${value.target_flow}` : null,
    value.consultation_window ? `Preferred consultation window (not a confirmed appointment): ${value.consultation_window}` : null,
    value.project_details ? `Project details: ${value.project_details}` : null,
  ].filter(Boolean);

  return details.length ? details.join("\n") : undefined;
}
